import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Shield, Landmark, Briefcase, CheckCircle2, Globe, Building2, Gem, Hexagon, Layers, Mountain, Target, Zap } from 'lucide-react';
const Hero = () => {
  return <section className="relative min-h-screen bg-brand-navy overflow-hidden" data-id="element-228" data-mp="2x5za91r8cbft">
      {/* Scroll Reveal Animation */}
      <motion.div initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1
    }} className="absolute inset-0 z-0 pointer-events-none" data-id="element-229" data-mp="1ziz558mk8ptr" />

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-12 relative z-10 grid lg:grid-cols-2 gap-12 items-start" data-id="element-230" data-mp="29etxivgl5z9q">
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8
      }} className="flex flex-col" data-id="element-231" data-mp="2dfdsbsdosqsp">
          <span className="inline-block text-brand-gold font-heading mt-6 font-bold tracking-[0.2em] uppercase text-[10px] mb-4 order-1" data-id="element-232" data-mp="3oyymhd0c1lkh">
            Three Practices. One Trusted Advisor.
          </span>
          <h1 className="text-4xl md:text-5xl font-display text-white leading-[1.1] mb-6 order-2" data-id="element-233" data-mp="2vvvnyan9gpdt">
            Strategic Advice for <br data-id="element-234" data-mp="2mxa6yjxftif5" />
            <span className="italic" data-id="element-235" data-mp="2mxs1nc4gboky">Complex</span> Financial Decisions
          </h1>

          <div className="flex flex-col md:flex-row flex-wrap gap-4 order-3 mt-5 md:mt-0 md:order-4 mb-8 md:mb-0" data-id="element-236" data-mp="2hyffktlemljf">
            <Link to="/contact" className="w-full md:w-auto bg-brand-gold hover:bg-brand-gold/90 text-white px-5 py-4 md:py-3 text-sm md:text-xs font-bold tracking-widest uppercase transition-all flex items-center justify-center md:justify-start gap-3" data-id="element-237" data-mp="2d6xbhwo344u8">
              Schedule a Consultation <ArrowRight size={16} data-id="element-238" data-mp="1z64k3kt2xpvc" />
            </Link>
            <Link to="/services" className="w-full md:w-auto border border-white/30 hover:border-white text-white px-6 py-4 md:py-3 text-sm md:text-xs font-bold tracking-widest uppercase transition-all text-center" data-id="element-239" data-mp="1u8l52iok592c">
              Explore Our Services
            </Link>
          </div>

          <p className="text-base text-white/70 font-light max-w-lg mb-0 md:mb-8 leading-relaxed order-4 md:order-3" data-id="element-240" data-mp="1ankxobnkmvgd">
            We help businesses grow through strategic advisory, capital
            solutions, and tax expertise. Delivering practical advice and
            disciplined execution.
          </p>

          <div className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-white/10 pt-8 order-5" data-id="element-241" data-mp="28906oaqa2q2g">
            {[{
            label: 'Strategic Advisory',
            value: 'Expertise'
          }, {
            label: 'Tax & Accounting',
            value: 'Precision'
          }, {
            label: 'Capital Solutions',
            value: 'Growth'
          }].map((stat, i) => <div key={i} data-id="element-242" data-mp="2r1b2jkmilnr2" data-mpi={i}>
                <div className="text-brand-gold font-display text-2xl mb-1" data-id="element-243" data-mp="2r1kuvev186zx">
                  {stat.value}
                </div>
                <div className="text-white/50 text-xs uppercase tracking-widest" data-id="element-244" data-mp="fjpn1q5bjl2r">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Right Column - Image and Floating Cards */}
        <div className="flex flex-col gap-4" data-id="element-245" data-mp="kdll0sll17xx">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative hidden lg:block" data-id="element-246" data-mp="kewmumvgw5me">
            <div className="relative z-0 rounded-2xl overflow-hidden shadow-2xl border border-white/10" data-id="element-247" data-mp="5o5wlj9ixele">
              <img src="https://images.pexels.com/photos/5685770/pexels-photo-5685770.jpeg" alt="Strategic Meeting" className="w-full h-[250px] md:h-[350px] object-cover" data-id="element-248" data-mp="oa0s7azctgx" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent z-0" data-id="element-249" data-mp="1c3gkhi35me0b" />
            </div>
          </motion.div>

          {/* Floating Cards - Now positioned below the image */}
          <div className="hidden lg:grid grid-cols-2 gap-4" data-id="element-250" data-mp="2cf2fgrwwdaa1">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="bg-white p-4 rounded-xl shadow-2xl" data-id="element-251" data-mp="2hb184rp9deg9">
              <div className="flex items-center gap-3 mb-2" data-id="element-252" data-mp="2qawodaxo7zn5">
                <div className="p-2 bg-brand-gold/10 rounded-lg text-brand-gold" data-id="element-253" data-mp="2vaj17j9l7dvf">
                  <TrendingUp size={20} data-id="element-254" data-mp="303lnd6c1bl1l" />
                </div>
                <div className="text-brand-navy font-bold text-base" data-id="element-255" data-mp="351nfshvcowpq">
                  Growth Capital
                </div>
              </div>
              <div className="text-brand-charcoal/60 text-xs leading-relaxed" data-id="element-256" data-mp="3a21f9bivyyc5">
                Tailored financial structures to fuel your expansion goals.
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="bg-brand-emerald p-4 rounded-xl shadow-2xl" data-id="element-257" data-mp="12frkwu7s3kdr">
              <div className="flex items-center gap-3 mb-2" data-id="element-258" data-mp="of7423lfbymw">
                <div className="p-2 bg-white/10 rounded-lg text-white" data-id="element-259" data-mp="oh1n1cd4xgoc">
                  <Shield size={20} data-id="element-260" data-mp="1m1xp0prqc7b6" />
                </div>
                <div className="text-white font-bold text-base" data-id="element-261" data-mp="1h3w4e0ennzxl">Tax Planning</div>
              </div>
              <div className="text-white/80 text-xs leading-relaxed" data-id="element-262" data-mp="1cd5zspe2013y">
                Strategic tax optimization for corporate and private clients.
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
};
const TrustedBy = () => {
  const logos = [{
    icon: <Globe size={28} data-id="element-263" data-mp="2030d182abroq" />,
    name: 'Global Finance'
  }, {
    icon: <Building2 size={28} data-id="element-264" data-mp="201fgxe0bo0or" />,
    name: 'Apex Industries'
  }, {
    icon: <Gem size={28} data-id="element-265" data-mp="3uaca8larlp1r" />,
    name: 'Nexus Capital'
  }, {
    icon: <Hexagon size={28} data-id="element-266" data-mp="3ucyh0ipu00ix" />,
    name: 'Vertex Holdings'
  }, {
    icon: <Zap size={28} data-id="element-267" data-mp="25i6cmp7gbvg" />,
    name: 'Quantum Ventures'
  }, {
    icon: <Layers size={28} data-id="element-268" data-mp="3kl12b1zm6qfg" />,
    name: 'Meridian Partners'
  }, {
    icon: <Target size={28} data-id="element-269" data-mp="3kkj0lpu6nk05" />,
    name: 'Stellar Group'
  }, {
    icon: <Mountain size={28} data-id="element-270" data-mp="3pj2klkp7ulpv" />,
    name: 'Horizon Enterprises'
  }];
  return <section className="py-12 bg-white border-b border-brand-navy/5 overflow-hidden" data-id="element-271" data-mp="3amv2qr1a5wdr">
      <div className="max-w-7xl mx-auto px-6 mb-8 text-center" data-id="element-272" data-mp="3aoxzrv0uoprz">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand-charcoal/40" data-id="element-273" data-mp="3fn9b6a6sn8cb">
          Trusted by industry leaders worldwide
        </p>
      </div>
      <div className="relative flex overflow-x-hidden" data-id="element-274" data-mp="36ki0wdpnmim9">
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" data-id="element-275" data-mp="zutldbpkhw6x" />
        
        <motion.div className="flex whitespace-nowrap gap-16 px-8 items-center" animate={{
        x: ['0%', '-50%']
      }} transition={{
        repeat: Infinity,
        ease: 'linear',
        duration: 60
      }} data-id="element-276" data-mp="14twqsx9gm4ff">
          {[...logos, ...logos, ...logos].map((logo, i) => <div key={i} className="flex items-center gap-3 text-brand-navy/30 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 cursor-default" data-id="element-277" data-mp="1ns003a3occp9" data-mpi={i}>
              {logo.icon}
              <span className="text-xl md:text-2xl font-display font-bold tracking-widest uppercase" data-id="element-278" data-mp="1sprnprxadcnn">
                {logo.name}
              </span>
            </div>)}
        </motion.div>

        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" data-id="element-279" data-mp="22nnzmly1sbmt" />
      </div>
    </section>;
};
const ExecutiveSummary = () => {
  return <section className="py-24 bg-brand-warm-white overflow-hidden relative" data-id="element-280" data-mp="8agb1c0o0w9m">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto px-6" data-id="element-281" data-mp="wuv0221yulx3">
        <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-282" data-mp="dv7cznnwanwo">
          <div className="relative" data-id="element-283" data-mp="dx1vkvbp23xl">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl" data-id="element-284" data-mp="iu0nr8bss0t5">
              <img src="https://cdn.magicpatterns.com/uploads/q1ZMwEfNCV87sM66sQwwn1/Emmanuel_B_Ogunsalu_2_(Final).jpg" alt="Executive Leadership" className="w-full h-full object-cover" data-id="element-285" data-mp="iw508enri851" />
            </div>
            <div className="absolute -bottom-10 -right-10 bg-brand-navy p-12 rounded-2xl shadow-2xl hidden md:block max-w-md" data-id="element-286" data-mp="1yaiy8ryl0cmb">
              <div className="text-brand-gold mb-6" data-id="element-287" data-mp="23bymstzyx19g">
                <svg width="40" height="30" viewBox="0 0 40 30" fill="currentColor" data-id="element-288" data-mp="2dcr60fcphfub">
                  <path d="M0 18.5C0 8.3 7.8 0 17.4 0V6.5C12.5 6.5 8.5 10.5 8.5 15.4H17.4V30H0V18.5ZM22.6 18.5C22.6 8.3 30.4 0 40 0V6.5C35.1 6.5 31.1 10.5 31.1 15.4H40V30H22.6V18.5Z" data-id="element-289" data-mp="23nr2cin9dw9s" />
                </svg>
              </div>
              <p className="text-white text-xl font-display italic leading-relaxed mb-6" data-id="element-290" data-mp="1uodutvip3hrp">
                "Delivering practical advice, disciplined execution, and lasting
                partnerships."
              </p>

              <div className="h-px w-12 bg-brand-gold mb-4" data-id="element-291" data-mp="1kpol4q0yd3mj" />
              <div className="text-white/60 text-sm uppercase tracking-widest font-bold" data-id="element-292" data-mp="1gol3pz39azj7">
                Consilium Philosophy
              </div>
            </div>
          </div>

          <div data-id="element-293" data-mp="3b2421b542m5k">
            <span className="text-brand-emerald font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-294" data-mp="3b1kt2jxfv83o">
              Our Identity
            </span>
            <h2 className="text-4xl md:text-5xl font-display text-brand-navy mb-8 leading-tight" data-id="element-295" data-mp="cqo4lnpms4uk">
              A Multidisciplinary Advisory Firm Built on Trust
            </h2>
            <div className="space-y-6 text-brand-charcoal/70 text-lg leading-relaxed" data-id="element-296" data-mp="mm96llup954b">
              <p data-id="element-297" data-mp="hozgs53eerfy">
                Consilium Advisory is more than a consulting firm. We are
                strategic partners dedicated to navigating the complexities of
                modern finance and business growth for a global clientele.
              </p>
              <p data-id="element-298" data-mp="3oez5c97z1o1">
                Our approach combines deep financial expertise with strategic
                insight and real-world investment experience. We serve
                high-net-worth individuals, institutional investors, and
                entrepreneurs who demand institutional quality and personal
                attention.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-299" data-mp="1hvutd5h7zsrc">
              {[{
              title: 'Financial Expertise',
              desc: 'Rigorous analysis and technical mastery across tax and accounting.'
            }, {
              title: 'Strategic Insight',
              desc: 'Forward-looking guidance to navigate market shifts and opportunities.'
            }, {
              title: 'Investment Experience',
              desc: 'Practical knowledge from managing complex capital structures.'
            }, {
              title: 'Global Reach',
              desc: 'U.S. and Nigerian expertise for cross-border success.'
            }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-300" data-mp="g5zh7cwab4tq" data-mpi={i}>
                  <div className="mt-1 text-brand-gold" data-id="element-301" data-mp="g8lp5dn37mch">
                    <CheckCircle2 size={20} data-id="element-302" data-mp="l6n71v8s1fmd" />
                  </div>
                  <div data-id="element-303" data-mp="2muhz8o5l1dwk">
                    <h4 className="font-heading font-bold text-brand-navy mb-1" data-id="element-304" data-mp="2mv16naxlwjwc">
                      {item.title}
                    </h4>
                    <p className="text-sm text-brand-charcoal/60" data-id="element-305" data-mp="2hwgdtwkop2hx">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
};
const Practices = () => {
  const practices = [{
    slug: 'tax-and-accounting',
    icon: <Landmark className="w-8 h-8" data-id="element-306" data-mp="1z45hklkjc2ss" />,
    title: 'Tax & Accounting',
    description: 'Precision-driven tax planning and accounting services for corporate and private wealth clients.',
    features: ['Tax Optimization', 'Financial Reporting', 'Compliance']
  }, {
    slug: 'strategic-advisory',
    icon: <Briefcase className="w-8 h-8" data-id="element-307" data-mp="3ojlhdackkc60" />,
    title: 'Strategic Advisory',
    description: 'Comprehensive business strategy, market entry, and operational optimization for sustainable growth.',
    features: ['Market Analysis', 'Growth Strategy', 'Operational Excellence']
  }, {
    slug: 'consilium-capital',
    icon: <TrendingUp className="w-8 h-8" data-id="element-308" data-mp="1g1muuw0n4yts" />,
    title: 'Consilium Capital',
    description: 'Navigating complex capital structures, debt advisory, and equity raising for ambitious enterprises.',
    features: ['Debt Advisory', 'Equity Raising', 'Capital Structuring']
  }];
  return <section className="py-24 bg-white relative overflow-hidden" data-id="element-309" data-mp="sc0w29o6klmb">
      <div className="max-w-7xl mx-auto px-6" data-id="element-310" data-mp="wcumb9p3icid">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-311" data-mp="wb1dbnr0fg77">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-brand-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block" data-id="element-312" data-mp="11c78ds857fjw">
            Our Core Practices
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl font-display text-brand-navy mb-6" data-id="element-313" data-mp="26bw0mbiz19m5">
            Specialized Expertise for Global Ambitions
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-brand-charcoal/60 text-lg" data-id="element-314" data-mp="33xb8u8dut076">
            We provide a unified approach to business challenges, combining
            strategic foresight with technical financial mastery.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-315" data-mp="3ilbj4tzyxle6">
          {practices.map((practice, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} whileHover={{
          y: -10
        }} className="bg-brand-warm-white p-10 rounded-2xl border border-brand-navy/5 hover:shadow-2xl transition-all duration-500 group flex flex-col" data-id="element-316" data-mp="3mmdbcqvgwx6n" data-mpi={idx}>
              <div className="text-brand-gold mb-8 group-hover:scale-110 transition-transform duration-500" data-id="element-317" data-mp="2uufmnjkki20l">
                {practice.icon}
              </div>
              <h3 className="text-2xl font-display text-brand-navy mb-4" data-id="element-318" data-mp="voino2h6tcx0">
                {practice.title}
              </h3>
              <p className="text-brand-charcoal/70 mb-8 leading-relaxed" data-id="element-319" data-mp="qonx601bqpch">
                {practice.description}
              </p>
              <ul className="space-y-3 mb-8 flex-grow" data-id="element-320" data-mp="mpf4yzlxmzj1">
                {practice.features.map((feature, fIdx) => <li key={fIdx} className="flex items-center gap-3 text-sm font-medium text-brand-navy/80" data-id="element-321" data-mp="1jc0he3k0il2m" data-mpi={fIdx}>
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-gold" data-id="element-322" data-mp="1ac3nnnirmw0p" />
                    {feature}
                  </li>)}
              </ul>
              <Link to={`/services/${practice.slug}`} className="mt-auto inline-flex items-center gap-2 text-brand-navy font-bold tracking-widest uppercase text-xs border-b-2 border-brand-gold pb-1 group-hover:gap-4 transition-all self-start" data-id="element-323" data-mp="3v46we62exnzd">
                Learn More <ArrowRight size={16} className="text-brand-gold" data-id="element-324" data-mp="2al3fu33umks7" />
              </Link>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const Insights = () => {
  return <section className="py-24 bg-brand-navy text-white overflow-hidden" data-id="element-325" data-mp="2je6oly3ju9gi">
      <div className="max-w-7xl mx-auto px-6" data-id="element-326" data-mp="2od02hrmr02h7">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8" data-id="element-327" data-mp="2oc88gl8suct1">
          <div className="max-w-2xl" data-id="element-328" data-mp="2tb1ig3ovnlog">
            <motion.span initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="text-brand-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block" data-id="element-329" data-mp="2te78bl5byrl8">
              Knowledge & Perspectives
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-4xl md:text-5xl font-display leading-tight" data-id="element-330" data-mp="p184699wchuv">
              Strategic Insights for the <br data-id="element-331" data-mp="64gcbbgaguf7" />
              <span className="italic text-brand-gold" data-id="element-332" data-mp="63x4inkcrmew">Modern Economy</span>
            </motion.h2>
          </div>
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-333" data-mp="16n5cg75vwdv">
            <Link to="/insights" className="text-brand-gold font-bold tracking-widest uppercase text-xs flex items-center gap-3 border-b border-brand-gold/30 pb-2 hover:border-brand-gold transition-all" data-id="element-334" data-mp="1qiz42gh11q4n">
              View All Insights <ArrowRight size={16} data-id="element-335" data-mp="1u3itfy9zp913" />
            </Link>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-336" data-mp="1y0wyreujuwnt">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="group cursor-pointer" data-id="element-337" data-mp="1k41pbladz378">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-8" data-id="element-338" data-mp="2hqh76o6q62vb">
              <img src="https://cdn.magicpatterns.com/patterns/generated-images/5d380a82-65a4-4d77-9f46-183be55eaef6.jpg" alt="Global market analysis displayed in a boardroom overlooking an emerging-market city" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-339" data-mp="231b7z07w0cnf" />
              <div className="absolute top-6 left-6 bg-brand-gold text-white px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full" data-id="element-340" data-mp="vwoqtrchpqlo">
                Strategy
              </div>
            </div>
            <h3 className="text-2xl font-display mb-4 group-hover:text-brand-gold transition-colors" data-id="element-341" data-mp="m86srhvlhxlq">
              Navigating Cross-Border Investments in Emerging Markets
            </h3>
            <p className="text-white/60 leading-relaxed mb-6" data-id="element-342" data-mp="c8ppf73tfvsx">
              A comprehensive guide to identifying opportunities and managing
              risks in high-growth corridors between the US and West Africa.
            </p>
            <div className="flex items-center gap-4 text-xs font-bold tracking-widest uppercase text-white/40" data-id="element-343" data-mp="3a3xwmgd96g0">
              <span data-id="element-344" data-mp="87w14cmgoezi">June 12, 2024</span>
              <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-345" data-mp="1xmtuxd216vrm" />
              <span data-id="element-346" data-mp="1xkhckysd2je1">8 Min Read</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="group cursor-pointer" data-id="element-347" data-mp="22l537jcogy7q">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-8" data-id="element-348" data-mp="2gqdu9bilauag">
              <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80" alt="Insight 2" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-349" data-mp="2vf0jq61xl2fe" />
              <div className="absolute top-6 left-6 bg-brand-emerald text-white px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full" data-id="element-350" data-mp="30ewlrzr2ijsb">
                Tax & Finance
              </div>
            </div>
            <h3 className="text-2xl font-display mb-4 group-hover:text-brand-gold transition-colors" data-id="element-351" data-mp="162gin7tkjlro">
              The Future of Corporate Tax Optimization in a Digital Age
            </h3>
            <p className="text-white/60 leading-relaxed mb-6" data-id="element-352" data-mp="3a619abhegz6o">
              How modern enterprises are restructuring their financial
              operations to adapt to evolving global tax regulations and digital
              transformation.
            </p>
            <div className="flex items-center gap-4 text-xs font-bold tracking-widest uppercase text-white/40" data-id="element-353" data-mp="3o4r7xvjvb6x5">
              <span data-id="element-354" data-mp="3t3c9byauvwn6">May 28, 2024</span>
              <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-355" data-mp="3t29jltp9s08u" />
              <span data-id="element-356" data-mp="1on2wkbp8sy3">12 Min Read</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
const CTA = () => {
  return <section className="py-24 bg-brand-warm-white relative overflow-hidden" data-id="element-357" data-mp="6qccdvj9wyk2">
      <div className="max-w-5xl mx-auto px-6 text-center relative z-10" data-id="element-358" data-mp="boxe5ze6bqam">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-brand-navy py-10 px-4 md:p-24 rounded-[2rem] shadow-2xl relative overflow-hidden" data-id="element-359" data-mp="fo6937ky9u7r">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/10 rounded-full blur-[80px] -mr-32 -mt-32" data-id="element-360" data-mp="1r8ek80oanrco" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-emerald/10 rounded-full blur-[80px] -ml-32 -mb-32" data-id="element-361" data-mp="2juzhfhrgaxo5" />

          <h2 className="text-4xl md:text-5xl font-display text-white mb-8 relative z-10" data-id="element-362" data-mp="2evml86eowe8w">
            Ready to Elevate Your <br data-id="element-363" data-mp="2a44o5tfsext7" />
            <span className="italic text-brand-gold" data-id="element-364" data-mp="2a3let147dhqs">Strategic Position?</span>
          </h2>
          <p className="text-white/70 text-lg mb-12 max-w-2xl mx-auto relative z-10" data-id="element-365" data-mp="255jwiielpmgd">
            Partner with Consilium Advisory for institutional-quality guidance
            tailored to your unique financial and business objectives.
          </p>
          <motion.div whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="inline-block" data-id="element-366" data-mp="2icz9n8p4ijad">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold/90 text-white px-10 py-5 text-sm font-bold tracking-widest uppercase transition-all shadow-xl relative z-10 inline-block" data-id="element-367" data-mp="28etauzym5t9q">
              Schedule a Consultation
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>;
};
const MobileHeroImage = () => {
  return <div className="lg:hidden px-6 pt-12 bg-white" data-id="element-368" data-mp="2re775ojtfg3e">
      <div className="max-w-7xl mx-auto" data-id="element-369" data-mp="2rbb81ww8eldd">
        <div className="relative z-0 rounded-2xl overflow-hidden shadow-2xl border border-brand-navy/10" data-id="element-370" data-mp="2wbqk8l4s1mtr">
          <img src="https://images.pexels.com/photos/5685770/pexels-photo-5685770.jpeg" alt="Strategic Meeting" className="w-full h-[300px] object-cover" data-id="element-371" data-mp="3k1tbx04s4qru" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent z-0" data-id="element-372" data-mp="1dcsnhzf3un3d" />
        </div>
      </div>
    </div>;
};
export const Home = () => {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-373" data-mp="1m6ntf9o1whk2">
      <Hero data-id="element-374" data-mp="ue6mekjl8vzg" />
      <MobileHeroImage data-id="element-375" data-mp="ufzw68phzwbo" />
      <TrustedBy data-id="element-376" data-mp="uf87ziy8d0vf" />
      <ExecutiveSummary data-id="element-377" data-mp="ugt2x9ogiltt" />
      <Practices data-id="element-378" data-mp="pgetbn77j3to" />
      <Insights data-id="element-379" data-mp="pgx03drhx0fu" />
      <CTA data-id="element-380" data-mp="phg6bx7sa0e1" />
    </motion.div>;
};